<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <span class="text-h5">内镜品牌及医疗机构档案</span>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="exportData" class="mr-2">
          <v-icon left>mdi-download</v-icon>
          导出Excel
        </v-btn>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          新增档案
        </v-btn>
      </v-card-title>

      <!-- 搜索区域 -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchForm.hospitalName"
              label="医院名称"
              clearable
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchForm.province"
              label="省份"
              clearable
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchForm.department"
              label="使用科室"
              clearable
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" @click="searchData" class="mr-2">
              <v-icon left>mdi-magnify</v-icon>
              搜索
            </v-btn>
            <v-btn @click="resetSearch">
              <v-icon left>mdi-refresh</v-icon>
              重置
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 数据表格 -->
      <v-data-table
        :headers="headers"
        :items="tableData"
        :loading="loading"
        :server-items-length="total"
        :options.sync="options"
        class="elevation-1"
        @update:options="loadData"
      >
        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click="editItem(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="info" @click="viewItem(item)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="deleteItem(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- 新增/编辑对话框 -->
    <v-dialog v-model="dialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ isEdit ? '编辑' : '新增' }}内镜品牌档案</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-expansion-panels v-model="panel" multiple>
              <!-- 基本信息 -->
              <v-expansion-panel>
                <v-expansion-panel-header>基本信息</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="formData.hospitalId"
                        :items="hospitalOptions"
                        item-text="hospitalName"
                        item-value="id"
                        label="医院（可选）"
                        outlined
                        dense
                        clearable
                        @change="onHospitalChange"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="formData.hospitalName"
                        label="医院名称"
                        outlined
                        dense
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.province"
                        label="省份"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.department"
                        label="使用科室"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.communicationAddress"
                        label="通信地址"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- 客户基本信息 -->
              <v-expansion-panel>
                <v-expansion-panel-header>客户基本信息</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.mainManager"
                        label="主要负责人"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.phoneOrWechat"
                        label="电话或微信号"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.backboneStaff1"
                        label="骨干人员1"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.backbonePhone"
                        label="骨干人员电话"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.totalStaffCount"
                        label="科室总人数"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.cleaningStaffCount"
                        label="洗消人数"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.isCleaningStaffMobile"
                        :items="yesNoOptions"
                        label="洗消人数是否流动"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- 工作时间和镜子信息 -->
              <v-expansion-panel>
                <v-expansion-panel-header>工作时间和镜子信息</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="formData.checkTime"
                        label="检查时间"
                        outlined
                        dense
                        placeholder="例：8:00-17:00"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="formData.dailyPatientCount"
                        label="接诊量/天（例）"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.gastroscopeBrand"
                        label="胃镜品牌和数量"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.colonoscopeBrand"
                        label="肠镜品牌和数量"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="formData.specialEndoscopeBrand"
                        label="特诊内镜品牌和数量"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- 设备信息 -->
              <v-expansion-panel>
                <v-expansion-panel-header>设备信息</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.roomCount"
                        label="房间数"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.bedCount"
                        label="检查床位"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="formData.hasConsultationInteraction"
                        :items="yesNoOptions"
                        label="诊室交互"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.cleaningWorkstationBrand"
                        label="洗消工作站品牌"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.cleaningWorkstationCount"
                        label="洗消工作站数量"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.cleaningTraceBrand"
                        label="洗消追溯品牌"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="formData.cleaningTraceMethod"
                        :items="traceMethodOptions"
                        label="追溯方式"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- 系统信息 -->
              <v-expansion-panel>
                <v-expansion-panel-header>系统信息</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasCallSystem"
                        :items="yesNoOptions"
                        label="叫号系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasCallIntelligentProgram"
                        :items="yesNoOptions"
                        label="叫号智能程序"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasAppointmentSystem"
                        :items="yesNoOptions"
                        label="预约登记系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasAppointmentIntelligentProgram"
                        :items="yesNoOptions"
                        label="预约智能程序"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasSurgeryTraceSystem"
                        :items="yesNoOptions"
                        label="手术追溯系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasSurgeryTraceRelatedInfo"
                        :items="yesNoOptions"
                        label="手术追溯关联信息"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasRecoveryManagementSystem"
                        :items="yesNoOptions"
                        label="复苏管理系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasRecoveryIntelligentProgram"
                        :items="yesNoOptions"
                        label="复苏智能程序"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasPostoperativeEducationSystem"
                        :items="yesNoOptions"
                        label="术后宣教系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasPostoperativeIntelligentProgram"
                        :items="yesNoOptions"
                        label="术后宣教智能程序"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.hasFollowUpSystem"
                        :items="yesNoOptions"
                        label="随访系统"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="formData.followUpFeedbackEffect"
                        :items="effectOptions"
                        label="随访反馈效果"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">取消</v-btn>
          <v-btn color="primary" :disabled="!valid" @click="saveData">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 查看详情对话框 -->
    <v-dialog v-model="viewDialog" max-width="1000px">
      <v-card>
        <v-card-title>
          <span class="text-h6">档案详情</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
                <tr v-for="(value, key) in viewData" :key="key">
                  <td class="font-weight-bold" style="width: 200px;">{{ getFieldLabel(key) }}</td>
                  <td>{{ value || '-' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 删除确认对话框 -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">确认删除</v-card-title>
        <v-card-text>确定要删除这条档案记录吗？此操作不可恢复。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">取消</v-btn>
          <v-btn color="error" @click="confirmDelete">删除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'EndoscopeBrandArchive',
  data() {
    return {
      loading: false,
      dialog: false,
      viewDialog: false,
      deleteDialog: false,
      isEdit: false,
      valid: false,
      panel: [0], // 默认展开第一个面板
      tableData: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      searchForm: {
        hospitalName: '',
        province: '',
        department: ''
      },
      formData: this.getEmptyFormData(),
      viewData: {},
      deleteItem: null,
      hospitalOptions: [],
      headers: [
        { text: '医院名称', value: 'hospitalName', sortable: false },
        { text: '省份', value: 'province', sortable: false },
        { text: '使用科室', value: 'department', sortable: false },
        { text: '主要负责人', value: 'mainManager', sortable: false },
        { text: '电话/微信', value: 'phoneOrWechat', sortable: false },
        { text: '创建时间', value: 'createdAt', sortable: false },
        { text: '操作', value: 'actions', sortable: false, width: '150' }
      ],
      yesNoOptions: [
        { text: '是', value: '是' },
        { text: '否', value: '否' }
      ],
      traceMethodOptions: [
        { text: '无', value: '无' },
        { text: '无感RFID', value: '无感RFID' },
        { text: '主动刷卡NFC', value: '主动刷卡NFC' }
      ],
      effectOptions: [
        { text: '良好', value: '良好' },
        { text: '无', value: '无' }
      ],
      rules: {
        required: value => !!value || '此字段为必填项'
      }
    }
  },

  mounted() {
    this.loadHospitals()
    this.loadData()
  },

  methods: {
    getEmptyFormData() {
      return {
        hospitalId: null,
        hospitalName: '',
        province: '',
        department: '',
        communicationAddress: '',
        mainManager: '',
        phoneOrWechat: '',
        backboneStaff1: '',
        backbonePhone: '',
        totalStaffCount: null,
        cleaningStaffCount: null,
        isCleaningStaffMobile: '',
        checkTime: '',
        dailyPatientCount: null,
        gastroscopeBrand: '',
        colonoscopeBrand: '',
        specialEndoscopeBrand: '',
        roomCount: null,
        bedCount: null,
        hasConsultationInteraction: '',
        cleaningWorkstationBrand: '',
        cleaningWorkstationCount: null,
        cleaningTraceBrand: '',
        cleaningTraceMethod: '',
        cleaningMachineBrand: '',
        cleaningMachineModel: '',
        cleaningMachineCount: null,
        mirrorCabinetBrand: '',
        mirrorCabinetModel: '',
        mirrorCabinetCount: null,
        storageEnvironment: '',
        operationStandard: '',
        cleaningRecord: '',
        hasCallSystem: '',
        hasCallIntelligentProgram: '',
        hasAppointmentSystem: '',
        hasAppointmentIntelligentProgram: '',
        hasSurgeryTraceSystem: '',
        hasSurgeryTraceRelatedInfo: '',
        hasRecoveryManagementSystem: '',
        hasRecoveryIntelligentProgram: '',
        hasPostoperativeEducationSystem: '',
        hasPostoperativeIntelligentProgram: '',
        hasFollowUpSystem: '',
        followUpFeedbackEffect: ''
      }
    },

    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          ...this.searchForm
        }

        const response = await axios.get('/api/device/endoscope-brand-archive/list', { params })

        if (response.data.code === 200) {
          this.tableData = response.data.data.rows
          this.total = response.data.data.total
        } else {
          this.$message.error(response.data.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },

    async loadHospitals() {
      try {
        const response = await axios.get('/api/device/endoscope-brand-archive/hospitals')
        if (response.data.code === 200) {
          this.hospitalOptions = response.data.data
        }
      } catch (error) {
        console.error('获取医院列表失败:', error)
      }
    },

    searchData() {
      this.options.page = 1
      this.loadData()
    },

    resetSearch() {
      this.searchForm = {
        hospitalName: '',
        province: '',
        department: ''
      }
      this.searchData()
    },

    async exportData() {
      try {
        const params = this.searchForm
        const response = await axios.get('/api/device/endoscope-brand-archive/export', {
          params,
          responseType: 'blob'
        })

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `内镜品牌档案_${new Date().toISOString().slice(0, 10)}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },

    openCreateDialog() {
      this.isEdit = false
      this.formData = this.getEmptyFormData()
      this.panel = [0] // 重置展开状态
      this.dialog = true
    },

    editItem(item) {
      this.isEdit = true
      this.formData = { ...item }
      this.panel = [0] // 重置展开状态
      this.dialog = true
    },

    viewItem(item) {
      this.viewData = { ...item }
      this.viewDialog = true
    },

    deleteItem(item) {
      this.deleteItem = item
      this.deleteDialog = true
    },

    async confirmDelete() {
      try {
        const response = await axios.delete(`/api/device/endoscope-brand-archive/delete/${this.deleteItem.id}`)

        if (response.data.code === 200) {
          this.$message.success('删除成功')
          this.loadData()
        } else {
          this.$message.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        this.$message.error('删除失败')
      } finally {
        this.deleteDialog = false
        this.deleteItem = null
      }
    },

    async saveData() {
      if (!this.$refs.form.validate()) {
        return
      }

      try {
        let response
        if (this.isEdit) {
          response = await axios.put(`/api/device/endoscope-brand-archive/update/${this.formData.id}`, this.formData)
        } else {
          response = await axios.post('/api/device/endoscope-brand-archive/create', this.formData)
        }

        if (response.data.code === 200) {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.closeDialog()
          this.loadData()
        } else {
          this.$message.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      }
    },

    closeDialog() {
      this.dialog = false
      this.$refs.form.resetValidation()
    },

    onHospitalChange(hospitalId) {
      const hospital = this.hospitalOptions.find(h => h.id === hospitalId)
      if (hospital) {
        this.formData.hospitalName = hospital.hospitalName
        this.formData.province = hospital.province
      }
    },

    getFieldLabel(key) {
      const labels = {
        hospitalName: '医院名称',
        province: '省份',
        department: '使用科室',
        communicationAddress: '通信地址',
        mainManager: '主要负责人',
        phoneOrWechat: '电话或微信号',
        backboneStaff1: '骨干人员1',
        backbonePhone: '骨干人员电话',
        totalStaffCount: '科室总人数',
        cleaningStaffCount: '洗消人数',
        isCleaningStaffMobile: '洗消人数是否流动',
        checkTime: '检查时间',
        dailyPatientCount: '接诊量/天',
        gastroscopeBrand: '胃镜品牌',
        colonoscopeBrand: '肠镜品牌',
        specialEndoscopeBrand: '特诊内镜品牌',
        roomCount: '房间数',
        bedCount: '检查床位',
        hasConsultationInteraction: '诊室交互',
        cleaningWorkstationBrand: '洗消工作站品牌',
        cleaningWorkstationCount: '洗消工作站数量',
        cleaningTraceBrand: '洗消追溯品牌',
        cleaningTraceMethod: '追溯方式',
        cleaningMachineBrand: '洗消机品牌',
        cleaningMachineModel: '洗消机型号',
        cleaningMachineCount: '洗消机数量',
        mirrorCabinetBrand: '挂镜柜品牌',
        mirrorCabinetModel: '挂镜柜型号',
        mirrorCabinetCount: '挂镜柜数量',
        storageEnvironment: '存储环境',
        operationStandard: '操作规范程度',
        cleaningRecord: '洗消记录',
        hasCallSystem: '叫号系统',
        hasCallIntelligentProgram: '叫号智能程序',
        hasAppointmentSystem: '预约登记系统',
        hasAppointmentIntelligentProgram: '预约智能程序',
        hasSurgeryTraceSystem: '手术追溯系统',
        hasSurgeryTraceRelatedInfo: '手术追溯关联信息',
        hasRecoveryManagementSystem: '复苏管理系统',
        hasRecoveryIntelligentProgram: '复苏智能程序',
        hasPostoperativeEducationSystem: '术后宣教系统',
        hasPostoperativeIntelligentProgram: '术后宣教智能程序',
        hasFollowUpSystem: '随访系统',
        followUpFeedbackEffect: '随访反馈效果',
        createdAt: '创建时间',
        updatedAt: '更新时间'
      }
      return labels[key] || key
    }
  }
}
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}

.v-expansion-panel-content {
  padding: 16px;
}
</style>
